'use client'

import { useEffect, useRef, useState } from 'react'
import type {
	ImageFragment,
	InternalVideoComponentFragment,
} from '@/lib/types/datocms/graphql/generated'
import Icon from './svg/icon'
import Image from 'next/image'
import clsx from 'clsx'

export default function InternalVideoComponent({
	video,
	coverImage,
}: Readonly<InternalVideoComponentFragment>) {
	const videoRef = useRef<HTMLVideoElement>(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const [duration, setDuration] = useState<string>('')

	useEffect(() => {
		const lazyVideoObserver = new IntersectionObserver(function (entries, _observer) {
			entries.forEach(function (videoEntry) {
				if (videoEntry.isIntersecting) {
					if (!videoRef.current?.src) {
						const videoElement = videoEntry.target as HTMLVideoElement
						videoElement.src = videoElement.dataset.src!

						for (const index in videoElement.children) {
							const childElement = videoElement.children[index]
							if (typeof childElement.nodeName === 'string' && childElement.nodeName === 'SOURCE') {
								const sourceElement = childElement as HTMLSourceElement
								sourceElement.src = sourceElement.dataset.src!
							}
						}
					}
				} else {
					const videoElement = videoEntry.target as HTMLVideoElement
					videoElement.pause()
				}
			})
		})

		if (videoRef.current) {
			lazyVideoObserver.observe(videoRef.current)
			// Add loadedmetadata event listener
			videoRef.current.addEventListener('loadedmetadata', () => {
				const minutes = Math.floor(videoRef.current?.duration ? videoRef.current?.duration / 60 : 0)
				const seconds = Math.floor(videoRef.current?.duration ? videoRef.current?.duration % 60 : 0)
				setDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`)
			})
		}

		return () => {
			lazyVideoObserver.disconnect()
		}
	}, [])

	const togglePlayPause = () => {
		if (videoRef.current) {
			if (isPlaying) {
				videoRef.current.pause()
			} else {
				videoRef.current.play()
			}
			setIsPlaying(!isPlaying)
		}
	}

	if (!video?.video?.mp4Url) return null

	return (
		<div className="video-container relative rounded-xl overflow-hidden mt-12">
			<video
				ref={videoRef}
				poster={video.video.thumbnailUrl}
				width={video.video.width}
				height={video.video.height}
				playsInline
				data-src={video.video.mp4Url}
				className="mx-auto flex w-full items-center justify-center aspect-video"
				controls={isPlaying}
			>
				<track kind="captions" />
				<source type={video.mimeType} data-src={video.video.mp4Url} />
			</video>
			{!isPlaying ? (
				<VideoOverlay
					isPlaying={isPlaying}
					togglePlayPause={togglePlayPause}
					title={video.videoTitle ?? video.alt ?? ''}
					duration={duration}
					coverImage={coverImage?.[0] as ImageFragment}
				/>
			) : null}
		</div>
	)
}

export function VideoOverlay({
	isPlaying,
	togglePlayPause,
	title,
	duration,
	coverImage,
}: {
	isPlaying: boolean
	togglePlayPause: () => void
	title: string | null | undefined
	duration?: string
	coverImage: ImageFragment | null | undefined
}) {
	return (
		<button
			className={clsx(
				'absolute inset-0 p-2  text-white z-10',
				isPlaying ? 'bg-black/0' : 'bg-black/20'
			)}
			onClick={togglePlayPause}
		>
			{coverImage?.url && (
				<Image
					src={coverImage.url}
					alt={title ?? ''}
					className="absolute inset-0 object-cover z-10"
					sizes="90vw"
					width={coverImage.width as number}
					height={coverImage.height as number}
				/>
			)}
			<div className="absolute bottom-10 left-10 flex items-center justify-center gap-4 z-20">
				<div className=" bg-white-frosted w-14 h-14 rounded-lg flex items-center justify-center hover:bg-white-frosted transition-colors duration-300">
					<Icon icon={isPlaying ? 'Pause' : 'Play'} className="w-4 h-4 text-blue" />
				</div>
				<span className="text-xs">
					{title} {duration && `(${duration})`}
				</span>
			</div>
		</button>
	)
}
